import {
  Chip,
  Grid,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import '../orders/Orders.scss';
import '../orders/UpcomingOrders.scss';
import moment from 'moment/moment';
import { Link, useSearchParams } from 'react-router-dom';
import CancelIcon from '@mui/icons-material/Cancel';
import { CalendarToday, Edit, Redeem, Schedule } from '@mui/icons-material';
import ConfirmationNumber from '@mui/icons-material/ConfirmationNumber';
import { AccountData, WaitingListItemData, WaitingListService, WaitingListSortBy } from '../api';
import { CellProps, useTable } from '../table/useTable';
import { useAuth } from '../session/InternalAuthProvider';
import { WorkshopCircle } from '../common/ui/WorkshopCircle';
import { useLanguage } from '../common/GeneralUtils';
import TimeUtils from '../common/TimeUtils';
import { getDateFormat, getTimeFormat } from '../common/getDateAndTimeFormat';
import { CalendarView, DATE_FORMAT } from '../calendar/calendarWrapper/CalendarWrapper';
import RotatingLoader from '../common/ui/RotatingLoader';
import { TABLE_PHONE_ID } from '../table/TableComponent';
import { useCalendar } from '../calendar/CalendarContext/CalendarContext';
import { WaitingListUpdateDialog } from './WaitingListUpdateDialog';
import EditOrderDialog from '../calendar/dialogs/EditOrderDialog/EditOrderDialog';
import OrderBoxDialog from '../calendar/calendar-items/OrderBoxDialog';

export const waitingListFetcher =
  (date?: string, sortBy: WaitingListSortBy = WaitingListSortBy.DATE) =>
  async ({ search, perPage, page }: { search?: string; page?: number; perPage: number }) =>
    WaitingListService.getWaitingList(undefined, page, perPage, search, date, sortBy);

export const WaitingListTable = React.memo(() => {
  const { users } = useCalendar();
  const { t } = useTranslation();
  const { editOrderDrawerState, closeEditOrderDrawer, openEditOrderDrawer } = useCalendar();
  const [orderOpen, setOrderOpen] = useState(false);
  const [orderId, setOrderId] = useState<string | undefined>(undefined);
  const [sortBy, setSortBy] = useState<WaitingListSortBy>(WaitingListSortBy.DATE);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [selectedWaitingListItem, setSelectedWaitingListItem] = useState<WaitingListItemData | undefined>();
  const [isUpdateDialogOpen, setIsUpdateDialogOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const qsDate = searchParams.get('date');
  const qsEmail = searchParams.get('email');
  const [searchDate, setSearchDate] = useState<string | undefined>(qsDate || undefined);
  const [searchEmail, setSearchEmail] = useState<string | undefined>(qsEmail || undefined);
  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;
  const timeFormat = getTimeFormat(account);
  const language = useLanguage();
  const { shortDateFormat } = getDateFormat(account);
  const hasWaitingListEnabled = account.experiences[0].order_pages.some(
    (orderPage) => orderPage.waiting_list?.enabled === true,
  );

  const tableDetails: CellProps<WaitingListItemData>[] = [
    {
      id: 'orderDate',
      alignment: 'center',
      label: (
        <Grid
          container
          alignItems="center"
          sx={{ cursor: 'pointer' }}
          onClick={() => setSortBy(WaitingListSortBy.DATE)}>
          <IconButton>
            <CalendarToday
              style={{
                color: sortBy === WaitingListSortBy.DATE ? '#67AD5B' : undefined,
              }}
            />
          </IconButton>
          <Typography
            style={{
              fontWeight: sortBy === WaitingListSortBy.DATE ? 700 : undefined,
              color: sortBy === WaitingListSortBy.DATE ? '#67AD5B' : undefined,
            }}>
            {t('waitinglist.askedDate')}
          </Typography>
        </Grid>
      ),
      render: (waitingListItem: WaitingListItemData) => {
        const momentDate = moment(waitingListItem.date);
        const calendarView = localStorage.getItem('webook-calendarview');
        // @ts-ignore
        const weekStart = momentDate.clone().startOf(calendarView === CalendarView.THREE_DAY ? 'day' : calendarView);
        return (
          <Link to={`/dashboard/calendar/${weekStart.format(DATE_FORMAT)}`}>{momentDate.format(shortDateFormat)}</Link>
        );
      },
    },
    {
      id: 'name',
      alignment: 'center',
      label: <Typography>{t('waitinglist.name')}</Typography>,
      render: (waitingListItem: WaitingListItemData) => (
        <Typography
          color={waitingListItem.email === searchEmail ? '#65A95A' : undefined}
          fontWeight={waitingListItem.email === searchEmail ? 700 : undefined}>
          {waitingListItem.firstname} {waitingListItem.lastname}
        </Typography>
      ),
    },
    {
      id: 'num_persons',
      alignment: 'center',
      label: <Typography>{t('waitinglist.num_persons')}</Typography>,
      render: (waitingListItem: WaitingListItemData) => waitingListItem.persons,
    },
    {
      id: TABLE_PHONE_ID,
      alignment: 'center',
      label: <Typography>{t('waitinglist.phone')}</Typography>,
      width: 140,
    },
    {
      id: 'email',
      alignment: 'center',
      label: <Typography>{t('waitinglist.email')}</Typography>,
      render: (waitingListItem: WaitingListItemData) => (
        <Typography style={{ textTransform: 'none' }}>{waitingListItem.email}</Typography>
      ),
    },
    {
      id: 'workshop',
      alignment: 'center',
      label: <Typography>{account.activity_label_singular?.[language] || ''}</Typography>,
      render: (waitingListItem: WaitingListItemData) => (
        <WorkshopCircle account={account} orderPageId={waitingListItem.order_page_id} language={language} withTooltip />
      ),
    },
    {
      id: 'createdat',
      alignment: 'center',
      label: (
        <Grid
          container
          alignItems="center"
          sx={{ cursor: 'pointer' }}
          onClick={() => setSortBy(WaitingListSortBy.CREATED_AT)}>
          <IconButton>
            <Schedule
              style={{
                color: sortBy === WaitingListSortBy.CREATED_AT ? '#67AD5B' : undefined,
              }}
            />
          </IconButton>
          <Typography
            style={{
              fontWeight: sortBy === WaitingListSortBy.CREATED_AT ? 700 : undefined,
              color: sortBy === WaitingListSortBy.CREATED_AT ? '#67AD5B' : undefined,
            }}>
            {t('waitinglist.createdat')}
          </Typography>
        </Grid>
      ),
      render: (waitingListItem: WaitingListItemData) => (
        <Typography>
          {TimeUtils.getLocalTime(waitingListItem.created_at, account.timezone).format(
            `${shortDateFormat} ${timeFormat}`,
          )}
        </Typography>
      ),
    },
    {
      id: 'status',
      alignment: 'center',
      label: <Typography>{t('waitinglist.statuss')}</Typography>,
      render: (waitingListItem: WaitingListItemData) => (
        <Typography>{t(`waitinglist.status.${waitingListItem.status?.toLowerCase()}`)}</Typography>
      ),
    },
    {
      id: 'comment',
      alignment: 'center',
      width: isSmallScreen ? 150 : undefined,
      label: <Typography>{t('waitinglist.comment')}</Typography>,
      render: (waitingListItem: WaitingListItemData) => (
        <Typography
          sx={{
            maxWidth: '250px',
            whiteSpace: 'normal',
            wordBreak: 'break-word',
          }}>
          {waitingListItem.comment || ''}
        </Typography>
      ),
    },
    {
      id: 'lastEditedBy',
      alignment: 'center',
      label: <Typography>{t('waitinglist.lastEditedBy')}</Typography>,
      render: (waitingListItem: WaitingListItemData) => {
        const user = users.find((user) => user.user_id === waitingListItem.last_updated_by_user_id);
        return <Typography noWrap>{user ? `${user.first_name} ${user.last_name}` : ''}</Typography>;
      },
    },
    {
      id: 'orderlink',
      alignment: 'left',
      width: 30,
      noPadding: true,
      label: <Typography>{t('vouchers.orderlink')}</Typography>,
      render: (waitingListItem: WaitingListItemData) =>
        waitingListItem.order_id ? (
          <Tooltip title={t('info')} arrow>
            <span>
              <IconButton
                onClick={() => {
                  setOrderOpen(true);
                  setOrderId(waitingListItem.order_id);
                }}>
                <ConfirmationNumber />
              </IconButton>
            </span>
          </Tooltip>
        ) : (
          ''
        ),
    },
    {
      id: 'redeem',
      alignment: 'left',
      width: 30,
      noPadding: true,
      render: (waitingListItem: WaitingListItemData) => (
        <Tooltip
          title={waitingListItem.order_id ? t('waitinglist.redeemdisabled') : t('waitinglist.convertToOrder')}
          arrow>
          <span>
            <IconButton
              onClick={() =>
                openEditOrderDrawer({
                  orderPageId: waitingListItem.order_page_id,
                  waitingListItem,
                })
              }
              disabled={!!waitingListItem.order_id}>
              <Redeem />
            </IconButton>
          </span>
        </Tooltip>
      ),
    },
    {
      id: 'edit',
      alignment: 'left',
      width: 30,
      noPadding: true,
      render: (waitingListItem: WaitingListItemData) => (
        <Tooltip title={t('edit')}>
          <span>
            <IconButton
              className="action-icon"
              onClick={() => {
                setSelectedWaitingListItem(waitingListItem);
                setIsUpdateDialogOpen(true);
              }}>
              <Edit />
            </IconButton>
          </span>
        </Tooltip>
      ),
    },
  ];

  const handleRemoveDate = () => {
    searchParams.delete('date');
    setSearchParams(searchParams);
    setSearchDate(undefined);
    setSearchEmail(undefined);
  };

  const { TableComponent, isLoadingData, mutate, data } = useTable<WaitingListItemData>({
    defaultRowsPerPage: 10,
    opId: `getWaitingList${searchDate}-${sortBy}`, // so searchrefreshes
    fetcher: waitingListFetcher(searchDate, sortBy),
    tableDetails,
    actionsComponent: (
      <Grid container alignItems="center" gap={2}>
        <ToggleButtonGroup value={sortBy} exclusive onChange={(_, value) => value && setSortBy(value)} size="small">
          <ToggleButton value={WaitingListSortBy.DATE}>
            <Tooltip title={t('waitinglist.sortByDate')}>
              <CalendarToday style={{ color: sortBy === WaitingListSortBy.DATE ? '#67AD5B' : undefined }} />
            </Tooltip>
          </ToggleButton>
          <ToggleButton value={WaitingListSortBy.CREATED_AT}>
            <Tooltip title={t('waitinglist.sortByCreated')}>
              <Schedule style={{ color: sortBy === WaitingListSortBy.CREATED_AT ? '#67AD5B' : undefined }} />
            </Tooltip>
          </ToggleButton>
        </ToggleButtonGroup>
        {searchDate && (
          <Chip
            color="warning"
            label={t('waitinglist.filteredForDate', { date: moment(searchDate).format(shortDateFormat) })}
            onDelete={handleRemoveDate}
            deleteIcon={<CancelIcon onMouseDown={(event) => event.stopPropagation()} />}
          />
        )}
      </Grid>
    ),
  });

  if (!hasWaitingListEnabled) {
    return (
      <Grid container>
        <Typography variant={'h6'}>{t('waitinglist.notEnabled')}</Typography>
      </Grid>
    );
  }
  if (isLoadingData) {
    return (
      <Grid
        container
        sx={{
          minHeight: '200px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
        <RotatingLoader />
      </Grid>
    );
  }
  return (
    <Grid container>
      {TableComponent}
      <WaitingListUpdateDialog
        open={isUpdateDialogOpen}
        setOpen={setIsUpdateDialogOpen}
        waitingListItem={selectedWaitingListItem}
        onUpdate={(updatedItem: WaitingListItemData) => {
          const items = data?.map((item) => (item.id === updatedItem.id ? updatedItem : item)) || [];
          void mutate(items);
        }}
      />
      <EditOrderDialog
        editOrderDialogState={editOrderDrawerState}
        closeEditOrderDialog={() => {
          void mutate();
          void closeEditOrderDrawer();
        }}
      />
      <OrderBoxDialog open={orderOpen} setOpen={setOrderOpen} orderId={orderId} />
    </Grid>
  );
});
